<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useI18n } from 'vue-i18n'

const router = useRouter()
const authStore = useAuthStore()
const { t } = useI18n()

const loading = ref(true)
const message = ref('')

onMounted(async () => {
  message.value = t('auth.logout.signing_out_message')
  
  try {
    // Clear local authentication state
    authStore.clearAuthentication()
    
    message.value = t('auth.logout.success_message')
    
    // Redirect to home page after a short delay
    setTimeout(() => {
      router.push('/')
    }, 2000)
    
  } catch (error) {
    console.error('Logout error:', error)
    message.value = t('auth.logout.logout_completed')
    
    // Still redirect to home even if there's an error
    setTimeout(() => {
      router.push('/')
    }, 2000)
  } finally {
    loading.value = false
  }
})

const goHome = () => {
  router.push('/')
}
</script>

<template>
  <div class="min-h-screen bg-gray-50 flex items-center justify-center px-4">
    <div class="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
      <!-- Loading State -->
      <div v-if="loading" class="space-y-6">
        <div class="flex justify-center">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
        
        <div>
          <h2 class="text-xl font-semibold text-gray-900 mb-2">
            {{ $t('auth.logout.signing_out') }}
          </h2>
          <p class="text-gray-600">
            {{ message }}
          </p>
        </div>
      </div>

      <!-- Success State -->
      <div v-else class="space-y-6">
        <div class="flex justify-center">
          <i class="fa-solid fa-check-circle text-green-500 text-4xl"></i>
        </div>
        
        <div>
          <h2 class="text-xl font-semibold text-gray-900 mb-2">
            {{ $t('auth.logout.signed_out') }}
          </h2>
          <p class="text-gray-600 mb-4">
            {{ message }}
          </p>
          
          <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p class="text-sm text-blue-700">
              <i class="fa-solid fa-info-circle mr-2"></i>
              {{ $t('auth.logout.redirect_message') }}
            </p>
          </div>
        </div>
        
        <button
          @click="goHome"
          class="w-full bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
        >
          <i class="fa-solid fa-home mr-2"></i>
          {{ $t('auth.logout.go_home') }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Loading animation for spinner */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}
</style>